import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appServices } from '@/data/AppServices';
import type { HomeDashboard } from '@/domain/Models';

export function HomePage() {
  const [dash, setDash] = useState<HomeDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    try {
      setDash(await appServices.study.dashboard());
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (error) return <div className="page error-box">{error}</div>;
  if (!dash?.today) return <div className="page muted">加载中…</div>;
  const task = dash.today;
  const minutes = Math.floor(task.studySeconds / 60);
  const resume = dash.resume[0];
  const progressed = task.newWordDone + task.reviewDone > 0;
  const primaryLabel = task.completed
    ? '再练一组词汇'
    : progressed
      ? '继续今日词汇'
      : '开始今日学习';

  return (
    <div className="page stack">
      <h2 style={{ margin: 0 }}>今日任务</h2>
      <div className="muted">
        连续学习 {dash.streakDays} 天 · 今日已学 {minutes} 分钟
      </div>
      <div className="row" style={{ gap: 8 }}>
        {dash.weekActivity.map((active, i) => (
          <span key={i} style={{ color: active ? 'var(--brand)' : 'var(--text-secondary)' }} aria-hidden>
            {active ? '●' : '○'}
          </span>
        ))}
      </div>

      {resume && (
        <section className="card stack">
          <strong>继续上次学习</strong>
          <div>
            {resume.kind === 'vocabulary'
              ? `词汇复习 · ${resume.subtitle}`
              : `情景「${resume.title}」 · ${resume.subtitle}`}
          </div>
          <button
            type="button"
            onClick={() => {
              if (resume.kind === 'vocabulary') navigate('/vocabulary/practice');
              else navigate(`/scenario/practice/${encodeURIComponent(resume.id)}`);
            }}
          >
            {resume.kind === 'vocabulary' ? '继续复习' : '继续情景'}
          </button>
        </section>
      )}

      <section className="card stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>新词学习</strong>
          <span>
            {task.newWordDone} / {task.newWordTarget}
          </span>
        </div>
        <div className="progress-bar">
          <span
            style={{
              width: `${task.newWordTarget ? Math.min(100, (task.newWordDone / task.newWordTarget) * 100) : 0}%`,
            }}
          />
        </div>
      </section>
      <section className="card stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>到期复习</strong>
          <span>
            {task.reviewDone} / {task.reviewTarget}
          </span>
        </div>
        <div className="progress-bar">
          <span
            style={{
              width: `${task.reviewTarget ? Math.min(100, (task.reviewDone / task.reviewTarget) * 100) : 100}%`,
            }}
          />
        </div>
      </section>

      {task.completed && (
        <div className="muted">今天的自动任务已完成，仍可自由复习词库或情景。</div>
      )}

      <button type="button" onClick={() => navigate('/vocabulary/practice')}>
        {primaryLabel}
      </button>
      <div className="grid-2">
        <button type="button" className="secondary" onClick={() => navigate('/vocabulary')}>
          去词库
        </button>
        <button type="button" className="secondary" onClick={() => navigate('/scenario')}>
          去对练
        </button>
      </div>

      {dash.recommendedScenario && (
        <section className="card stack">
          <strong>推荐情景</strong>
          <div>{dash.recommendedScenario.title}</div>
          <div className="muted">
            {dash.recommendedScenario.topic} · {dash.recommendedScenario.difficulty} · 约{' '}
            {dash.recommendedScenario.estimatedMinutes} 分钟
          </div>
          <button
            type="button"
            className="secondary"
            onClick={() =>
              navigate(`/scenario/practice/${encodeURIComponent(dash.recommendedScenario!.id)}`)
            }
          >
            进入情景
          </button>
        </section>
      )}
    </div>
  );
}
